package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.site.theme.*
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import org.w3c.files.get

/**
 * The Help screen: send feedback / request support. Shown INSIDE the main card as its own screen
 * (Index's ActiveView.HELP, opened from the avatar menu's "Help"), so the card's ← and ✕ do the
 * navigating; it used to be a centre-screen modal. The host owns where [onDone] goes. Mirrors
 * composeApp/.../components/FeedbackForm.kt.
 */
@Composable
fun FeedbackForm(
    onDone: () -> Unit,
    onSubmit: (message: String, imageBytes: ByteArray?, imageName: String?) -> Unit,
    initialName: String? = null,
    initialEmail: String? = null,
) {
    var name by remember { mutableStateOf(initialName ?: "") }
    var email by remember { mutableStateOf(initialEmail ?: "") }
    var message by remember { mutableStateOf("") }
    var submitted by remember { mutableStateOf(false) }
    var imageBytes by remember { mutableStateOf<ByteArray?>(null) }
    var imageName by remember { mutableStateOf<String?>(null) }

    // Same frame as the Account/Settings subscreens: 32px padding, centred title level with the
    // card's ← and ✕.
    Div(attrs = {
        style {
            padding(32.px)
            property("box-sizing", "border-box")
        }
    }) {
        if (!submitted) {
            // Header — same treatment as ProfileSubscreenScaffold's title
            Div(attrs = {
                style {
                    marginBottom(16.px)
                    property("text-align", "center")
                }
            }) {
                Span(attrs = {
                    style {
                        fontSize(14.px)
                        fontWeight(600)
                        color(Gray900)
                        property("letter-spacing", "0.02em")
                    }
                }) { Text("Support") }
            }

            // Name field
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                value(name)
                onInput { name = it.value }
                attr("placeholder", "Name")
                style {
                    width(100.percent)
                    padding(10.px, 12.px)
                    fontSize(13.px)
                    borderRadius(10.px)
                    property("border", "1px solid ${Gray300}")
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    marginBottom(8.px)
                }
            }

            // Email field
            Input(type = org.jetbrains.compose.web.attributes.InputType.Email) {
                value(email)
                onInput { email = it.value }
                attr("placeholder", "Email")
                style {
                    width(100.percent)
                    padding(10.px, 12.px)
                    fontSize(13.px)
                    borderRadius(10.px)
                    property("border", "1px solid ${Gray300}")
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    marginBottom(8.px)
                }
            }

            // Text area
            TextArea(attrs = {
                placeholder("What's on your mind? Bugs, suggestions, anything...")
                onInput { message = it.value }
                style {
                    width(100.percent)
                    property("min-height", "90px")
                    padding(12.px)
                    fontSize(13.px)
                    borderRadius(10.px)
                    property("border", "1px solid ${Gray300}")
                    property("outline", "none")
                    property("resize", "vertical")
                    property("font-family", "inherit")
                    property("box-sizing", "border-box")
                    property("transition", "border-color 0.2s ease")
                }
            })

            // Image upload row
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    marginTop(10.px)
                }
            }) {
                // Hidden file input
                Input(type = org.jetbrains.compose.web.attributes.InputType.File, attrs = {
                    id("feedback-image-input")
                    attr("accept", "image/*")
                    style { display(DisplayStyle.None) }
                    onChange {
                        val input = document.getElementById("feedback-image-input") as? HTMLInputElement
                        val file = input?.files?.get(0)
                        if (file != null) {
                            imageName = file.name
                            val reader = FileReader()
                            reader.onload = {
                                val result = reader.result
                                if (result is org.khronos.webgl.ArrayBuffer) {
                                    val view = org.khronos.webgl.DataView(result)
                                    imageBytes = ByteArray(view.byteLength) { i -> view.getInt8(i) }
                                }
                                Unit
                            }
                            reader.readAsArrayBuffer(file)
                        }
                    }
                })

                Span(attrs = {
                    style {
                        fontSize(13.px)
                        color(Gray500)
                        property("cursor", "pointer")
                    }
                    onClick {
                        (document.getElementById("feedback-image-input") as? HTMLInputElement)?.click()
                    }
                }) { Text("📎 Attach screenshot") }

                if (imageName != null) {
                    Span(attrs = {
                        style {
                            fontSize(12.px)
                            color(Primary)
                            fontWeight(600)
                        }
                    }) { Text(imageName ?: "") }
                }
            }

            // Send button
            Button(attrs = {
                onClick {
                    if (message.isNotBlank()) {
                        onSubmit(message, imageBytes, imageName)
                        submitted = true
                    }
                }
                style {
                    width(100.percent)
                    marginTop(14.px)
                    padding(10.px)
                    fontSize(14.px)
                    fontWeight(600)
                    color(Color.white)
                    property("background-color", "#0E7C7B")
                    property("border", "none")
                    borderRadius(10.px)
                    property("cursor", if (message.isBlank()) "default" else "pointer")
                    property("opacity", if (message.isBlank()) "0.5" else "1")
                    property("transition", "opacity 0.2s ease")
                }
            }) { Text("Send Feedback") }
        } else {
            // Thank you state
            Div(attrs = {
                style {
                    property("text-align", "center")
                    padding(24.px, 0.px)
                }
            }) {
                Div(attrs = {
                    style {
                        fontSize(18.px)
                        fontWeight(700)
                        color(Primary)
                        marginBottom(6.px)
                    }
                }) { Text("Thank you!") }
                P(attrs = {
                    style {
                        fontSize(13.px)
                        color(Gray500)
                        margin(0.px, 0.px, 14.px)
                    }
                }) { Text("Your feedback helps us improve. We'll be in touch via email.") }
                Button(attrs = {
                    onClick { onDone() }
                    style {
                        padding(8.px, 20.px)
                        fontSize(13.px)
                        fontWeight(600)
                        color(Color.white)
                        backgroundColor(Primary)
                        property("border", "none")
                        borderRadius(10.px)
                        property("cursor", "pointer")
                    }
                }) { Text("Close") }
            }
        }
    }
}
